import { useMemo } from "react";

import {
  SubscriptionStatus,
  useCurrentSubscriptionStateQuery,
} from "@/apollo/types";
import { useCurrentAccount } from "@/providers/account";
import dayjs from "@/utils/date";

export function useSubscriptionStatus() {
  const { data } = useCurrentSubscriptionStateQuery();
  const { status, customerId, subscriptionId } =
    data?.currentSubscriptionState ?? {};
  const trial = useTrialStatus(status ?? undefined);

  // A subscription is considered manually provisioned if there is no subscription ID
  // but the status is active. This indicates that the subscription is managed outside
  // of the standard billing system.
  // For example, via a feature flag.
  const isManuallyProvisioned =
    subscriptionId == null && status === SubscriptionStatus.Active;

  return {
    status: status ?? null,
    trial,
    customerId: customerId ?? null,
    subscriptionId: subscriptionId ?? null,
    isManuallyProvisioned,
  };
}

function useTrialStatus(subscriptionStatus: SubscriptionStatus | undefined) {
  const account = useCurrentAccount();

  return useMemo(() => {
    if (
      account.trialEndsAt == null ||
      subscriptionStatus === SubscriptionStatus.Active
    ) {
      return null;
    }

    const trialEndDate = new Date(account.trialEndsAt);
    const isExpired = dayjs(account.trialEndsAt).isBefore(dayjs());
    const daysLeft = Math.round(
      Math.max(0, dayjs(account.trialEndsAt).diff(dayjs(), "day", true)),
    );

    const isActive =
      subscriptionStatus === SubscriptionStatus.Freemium && !isExpired;

    return {
      isExpired,
      isActive,
      daysLeft,
      trialEndDate,
    };
  }, [account.trialEndsAt, subscriptionStatus]);
}

export function useIsFreemiumTrialExpired() {
  const { status, trial } = useSubscriptionStatus();
  return status === SubscriptionStatus.Freemium && trial?.isExpired === true;
}
